import React, { useState } from 'react';
import { Text, Button, Textarea } from "@geist-ui/core";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { sanitizeMarkdown } from '../../../utils/markdownUtils';

const MarkdownTest = () => {
  const [inputText, setInputText] = useState(
    "# Markdown Test Page\n\n" +
    "This page lets you test *italic* and **bold** formatting.\n\n" +
    "## Links and Navigation\n\n" +
    "This is a test of a navigation link: (go to 5)\n\n" +
    "This is a [normal markdown link](https://example.com)\n\n" +
    "### Lists\n\n" +
    "* Item 1\n" +
    "* Item 2\n* Item 3\n\n" +
    "1. Numbered item 1\n" +
    "2. Numbered item 2\n\n" +
    "> This is a blockquote in markdown\n\n" +
    "~~Strikethrough text~~\n\n" +
    "`Inline code block`\n\n" +
    "```\nCode block\nwith multiple lines\n```"
  );

  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };

  // Mock function for the "go to X" links
  const handleLinkClick = (e, sceneId) => {
    e.preventDefault();
    alert(`Navigate to scene ${sceneId}`);
  };

  // Process "go to X" links 
  const renderTextWithLinks = (text) => {
    const parts = text.split(/(go to \d+)/gi);
    return parts.map((part, index) => {
      const match = part.match(/go to (\d+)/i);
      if (match) {
        const targetSceneId = match[1];
        return (
          <a
            key={index}
            href="#"
            className="scene-link"
            onClick={e => handleLinkClick(e, targetSceneId)}
          >
            <Text span>{part}</Text>
          </a>
        );
      }
      return part;
    });
  };

  // Detects if a line has navigation links
  const hasNavigationLinks = (line) => {
    return line.match(/go to \d+/i);
  };

  return (
    <div className="markdown-test-page" style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
      <Text h1>Markdown Rendering Test</Text>
      
      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
        <div style={{ flex: 1 }}>
          <Text h3>Input (Edit markdown here)</Text>
          <Textarea 
            width="100%" 
            height="500px" 
            value={inputText} 
            onChange={handleInputChange}
            style={{ fontFamily: 'monospace' }}
          />
        </div>
        
        <div style={{ flex: 1 }}>
          <Text h3>Preview</Text>
          <div className="preview-content" style={{ 
            padding: '15px',
            border: '1px solid #ddd',
            borderRadius: '5px',
            minHeight: '500px',
            backgroundColor: 'white'
          }}>
            {inputText.split('\n\n').map((paragraph, index) => {
              // Check if paragraph contains navigation links
              if (hasNavigationLinks(paragraph)) {
                return (
                  <Text p key={index}>
                    {renderTextWithLinks(paragraph)}
                  </Text>
                );
              }
              
              // Otherwise render as markdown
              return (
                <div key={index} className="markdown-paragraph">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw, rehypeSanitize]}
                  >
                    {sanitizeMarkdown(paragraph)}
                  </ReactMarkdown>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      <div>
        <Text h3>Markdown Cheat Sheet</Text>
        <table className="markdown-cheatsheet">
          <tbody>
            <tr>
              <td><code>**bold text**</code></td>
              <td><strong>bold text</strong></td>
            </tr>
            <tr>
              <td><code>_italics_</code> or <code>*italics*</code></td>
              <td><em>italics</em></td>
            </tr>
            <tr>
              <td><code>## Heading</code></td>
              <td><h2 style={{margin:0}}>Heading</h2></td>
            </tr>
            <tr>
              <td><code>[Link text](https://example.com)</code></td>
              <td><a href="#">Link text</a></td>
            </tr>
            <tr>
              <td><code>- List item</code></td>
              <td>• List item</td>
            </tr>
            <tr>
              <td><code>1. Numbered list</code></td>
              <td>1. Numbered list</td>
            </tr>
            <tr>
              <td><code>~~strikethrough~~</code></td>
              <td><del>strikethrough</del></td>
            </tr>
            <tr>
              <td><code>`inline code`</code></td>
              <td><code>inline code</code></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MarkdownTest; 